import { useCallback } from "react";
import { useAudioContext } from "../../../domains/app/contexts/audio.context";
import { convertArticleToMediaData } from "../../../domains/audio/utils/convertArticleToMediaData";
import { gaEventArticleAudioComplete } from "../../../tools/analytics/domains/article.analytics";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import type { Api } from "../../../services/api";
import type { ArticlePageArticle } from "./useArticleData";
import type { ArticleAudioTrack } from "./useArticleAudio";

type SwipeArticle =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

/**
 * Single source of truth for triggering article narration playback: resolves
 * (or synthesizes on demand) the article's speech URL, queues the swipe list as
 * a track list, and opens the audio player. Drives the article hero's
 * play/pause (Listen) button.
 */
export function useArticleAudioPlayback({
  article,
  swipeList,
  synthesize,
}: {
  article: ArticlePageArticle | null;
  swipeList: SwipeArticle[];
  synthesize: () => Promise<ArticleAudioTrack | null>;
}): {
  isTrackPlaying: boolean;
  /** True when the loaded, actively-playing track is *this* article. */
  isThisArticlePlaying: boolean;
  /** Play/pause this article: resume-or-pause when it is the loaded track,
   *  otherwise (re)start narration from scratch. */
  toggleAudio: () => Promise<void>;
} {
  const {
    hasTrack: isTrackPlaying,
    setTracks,
    isPlaying,
    currentMediaContent,
    togglePlayPause,
  } = useAudioContext();

  const isThisArticleLoaded =
    article !== null &&
    currentMediaContent !== null &&
    currentMediaContent.source.id === article.id;
  const isThisArticlePlaying = isThisArticleLoaded && isPlaying;

  const playWithTrack = useCallback(
    (track: ArticleAudioTrack): void => {
      if (article === null) return;

      const tracks = (swipeList.length > 0 ? swipeList : [article]).flatMap(
        (a) => {
          const url =
            a.id === article.id
              ? track.url
              : a.speech !== null
                ? a.speech.url
                : null;
          if (url === null || url === "") return [];
          return [convertArticleToMediaData(a, url)];
        }
      );
      const trackIndex = tracks.findIndex(
        (track) => track.source.id === article.id
      );

      // Start playback docked as the mini player; the user expands the sheet
      // themselves — never auto-open it.
      setTracks({
        track,
        contentList: tracks,
        index: trackIndex === -1 ? 0 : trackIndex,
        autoplay: true,
        onEndReached: () => {
          gaEventArticleAudioComplete({
            article,
            listName: AnalyticsItemList.ARTICLE_PAGE,
          });
        },
      });
    },
    [article, swipeList, setTracks]
  );

  const handlePlayAudio = useCallback(async (): Promise<void> => {
    if (article === null) return;

    const cachedUrl = article.speech !== null ? article.speech.url : null;
    if (cachedUrl !== null && cachedUrl !== "") {
      playWithTrack({ url: cachedUrl, kind: "file" });
      return;
    }
    const track = await synthesize();
    if (track !== null) {
      playWithTrack(track);
    }
  }, [article, synthesize, playWithTrack]);

  const toggleAudio = useCallback(async (): Promise<void> => {
    // Already the loaded track → just flip play/pause without re-synthesizing.
    if (isThisArticleLoaded) {
      togglePlayPause();
      return;
    }
    await handlePlayAudio();
  }, [isThisArticleLoaded, togglePlayPause, handlePlayAudio]);

  return { isTrackPlaying, isThisArticlePlaying, toggleAudio };
}
